import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Subject, takeUntil, catchError, of, map, Observable } from 'rxjs';

import { WorkflowWebhookActionApiService } from '../services/workflow-webhook-action-api.service';
import { AuthService } from '../../../auth-pages/services/auth.service';
import { environment } from '../../../../../environments/environment';

import {
  WorkflowIntegrationActionType,
  WorkflowHttpMethod,
  WorkflowWebhookTestResponse,
  WorkflowWebhookEndpointResponse,
  WebhookKeyValueRow,
  WebhookTokenSuggestion,
  WEBHOOK_TEST_EXAMPLE,
  INBOUND_WEBHOOK_TEST_EXAMPLE,
  OBJECT_TYPE_LABELS,
  WEBHOOK_TOKEN_SUGGESTIONS,
  HTTP_STATUS_TEXT
} from '../models/workflow-webhook-action.model';

import { EntitySelectComponent } from '../../../../shared/components/entity-select/entity-select.component';
import { WorkflowRecordSelect } from '../shared/workflow-record-select.helper';
import { ContactoApiService } from '../../../contactos/services/contacto-api.service';
import { EmpresaApiService } from '../../../empresas/services/empresa-api.service';
import { DealApiService } from '../../../deals/services/deal-api.service';
import { QuoteApiService } from '../../../quotes/services/quote-api.service';
import { TicketsApiService } from '../../../tickets/services/tickets-api.service';
import { workflowRecordLabel, workflowStatusLabel } from '../models/workflow-labels';
interface RecordOption { id: number; label: string; }

/** Destino activo (último campo enfocado) donde se insertará el próximo token elegido. */
type ActiveField =
  | { kind: 'url' }
  | { kind: 'header' | 'body' | 'payload'; index: number }
  | null;

/** Resumen en lenguaje llano del resultado de un test outbound. */
interface OutboundSummary {
  kind: 'success' | 'error' | 'skipped' | 'unknown';
  title: string;
  detail: string;
}

@Component({
  selector: 'app-workflow-webhook-action-tester',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule, EntitySelectComponent],
  templateUrl: './workflow-webhook-action-tester.component.html'
})
export class WorkflowWebhookActionTesterComponent implements OnInit, OnDestroy {
  /** Etiquetas legibles: diccionario único del módulo (`models/workflow-labels.ts`). */
  readonly recordText = workflowRecordLabel;
  readonly statusText = workflowStatusLabel;

  activeTab: 'outbound' | 'inbound' = 'outbound';

  // ── Outbound ────────────────────────────────────────────────────────
  outActionType: WorkflowIntegrationActionType = 'SEND_WEBHOOK';
  outObjectType = 'CONTACT';
  outObjectId: number | null = null;
  recordSelect!: WorkflowRecordSelect;
  recordDisplayText = '';

  // Formulario estructurado (reemplaza el textarea "Config (JSON)" crudo).
  outUrl = '';
  outMethod: WorkflowHttpMethod = 'POST';
  outHeaders: WebhookKeyValueRow[] = [];
  outBodyFields: WebhookKeyValueRow[] = [];
  /** Si el Body avanzado no es un objeto plano (array/valor), se preserva aquí tal cual. */
  outBodyNonObject: any = undefined;
  outTimeoutSeconds: number | null = 10;
  /** Claves de config que no tienen UI estructurada propia (responseMapping, continueOnError, retry…). */
  outExtraConfig: Record<string, any> = {};

  outAdvancedOpen = false;
  outAdvancedJson = '';
  outAdvancedJsonError = '';

  outContextJson = '';
  outContextAdvancedOpen = false;
  outContextJsonError = '';

  /** Último campo (URL / fila de Headers / fila de Body) que tuvo foco, destino de los chips de tokens. */
  activeField: ActiveField = null;

  outLoading = false;
  outError = '';
  outResult: WorkflowWebhookTestResponse | null = null;

  outRecords: RecordOption[] = [];
  outRecordsLoading = false;

  // ── Inbound ─────────────────────────────────────────────────────────
  inEndpoints: WorkflowWebhookEndpointResponse[] = [];
  inEndpointsLoading = false;
  inSelectedEndpoint: WorkflowWebhookEndpointResponse | null = null;

  inObjectId: number | null = null;
  inRecords: RecordOption[] = [];
  inRecordsLoading = false;

  inSecret = '';

  // Formulario estructurado (reemplaza el textarea "Payload (JSON)" crudo).
  inPayloadFields: WebhookKeyValueRow[] = [];
  inPayloadNonObject: any = undefined;
  inAdvancedOpen = false;
  inAdvancedJson = '';
  inAdvancedJsonError = '';

  inLoading = false;
  inError = '';
  inResult: any = null;

  readonly outActionOptions: { value: WorkflowIntegrationActionType; label: string; disabled: boolean }[] = [
    { value: 'SEND_WEBHOOK',              label: 'Enviar webhook (saliente)',           disabled: false },
    { value: 'NATIVE_INTEGRATION_ACTION', label: 'Integración nativa (no disponible)',  disabled: true  },
    { value: 'CODE_SNIPPET',              label: 'Fragmento de código (no disponible)', disabled: true  }
  ];

  // INC_CRM_020 (8) inc26-46 — se conserva la sigla HTTP (es el estándar) con su significado al lado.
  readonly outMethodOptions: { value: WorkflowHttpMethod; label: string }[] = [
    { value: 'GET', label: 'GET — consultar datos' },
    { value: 'POST', label: 'POST — enviar / crear' },
    { value: 'PUT', label: 'PUT — reemplazar' },
    { value: 'PATCH', label: 'PATCH — actualizar' },
    { value: 'DELETE', label: 'DELETE — eliminar' }
  ];

  readonly objectTypeOptions: { value: string; label: string }[] = [
    { value: 'CONTACT', label: OBJECT_TYPE_LABELS['CONTACT'] },
    { value: 'COMPANY', label: OBJECT_TYPE_LABELS['COMPANY'] },
    { value: 'DEAL', label: OBJECT_TYPE_LABELS['DEAL'] },
    { value: 'QUOTE', label: OBJECT_TYPE_LABELS['QUOTE'] },
    { value: 'TICKET', label: OBJECT_TYPE_LABELS['TICKET'] }
  ];

  private destroy$ = new Subject<void>();

  constructor(
    private webhookApi: WorkflowWebhookActionApiService,
    private authService: AuthService,
    private contactoApi: ContactoApiService,
    private empresaApi: EmpresaApiService,
    private dealApi: DealApiService,
    private quoteApi: QuoteApiService,
    private ticketsApi: TicketsApiService,
    private route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    this.loadOutboundExample();
    this.inPayloadFields = this.objectToRows(INBOUND_WEBHOOK_TEST_EXAMPLE.payload);
    this.loadEndpointsAndPreselect();
  }

  private loadEndpointsAndPreselect(): void {
    this.inEndpointsLoading = true;
    this.webhookApi.listEndpoints().pipe(takeUntil(this.destroy$), catchError(() => of([]))).subscribe(list => {
      this.inEndpoints = list;
      this.inEndpointsLoading = false;

      const endpointId = Number(this.route.snapshot.queryParamMap.get('endpointId'));
      if (endpointId) {
        const ep = list.find(e => e.id === endpointId);
        if (ep) {
          this.activeTab = 'inbound';
          this.inSelectedEndpoint = ep;
          this.onEndpointSelected();
        }
      }
    });
    this.recordSelect = new WorkflowRecordSelect(
      this.contactoApi, this.empresaApi, this.dealApi, this.quoteApi, this.ticketsApi,
      () => this.outObjectType,
      () => this.authService.getCurrentUser()?.businessId || 1
    );
  }

  onOutObjectTypeChange(): void {
    this.outObjectId = null;
    this.recordDisplayText = '';
  }

  onRecordSelected(item: any | null): void {
    this.recordDisplayText = item ? this.recordSelect.label(item) : '';
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private get businessId(): number {
    return this.authService.getBusinessId() ?? 0;
  }

  get isBlockedAction(): boolean {
    return this.outActionType !== 'SEND_WEBHOOK';
  }

  get outResultJson(): string {
    return JSON.stringify(this.outResult?.output ?? {}, null, 2);
  }

  get outErrorJson(): string {
    return JSON.stringify(this.outResult?.error ?? {}, null, 2);
  }

  get inObjectTypeLabel(): string {
    const type = this.inSelectedEndpoint?.matchObjectType;
    return type ? (OBJECT_TYPE_LABELS[type] ?? type) : 'Desconocido';
  }

  /** URL pública completa que un sistema externo real usaría para este endpoint. */
  get inEndpointUrl(): string {
    if (!this.inSelectedEndpoint) return '';
    return `${environment.apiUrl}api/workflow-webhooks/inbound/${this.inSelectedEndpoint.endpointKey}`;
  }

  // ── Helpers genéricos clave-valor <-> objeto JSON ────────────────────

  private rowsToObject(rows: WebhookKeyValueRow[], smartParse = false): Record<string, any> {
    const out: Record<string, any> = {};
    for (const row of rows) {
      const key = row.key.trim();
      if (!key) continue;
      out[key] = smartParse ? this.smartParseValue(row.value) : row.value;
    }
    return out;
  }

  private smartParseValue(value: string): any {
    const trimmed = value.trim();
    if (trimmed === '') return '';
    if (trimmed === 'true') return true;
    if (trimmed === 'false') return false;
    if (trimmed === 'null') return null;
    if (/^-?\d+(\.\d+)?$/.test(trimmed)) {
      const n = Number(trimmed);
      if (!Number.isNaN(n)) return n;
    }
    return value;
  }

  private objectToRows(obj: unknown): WebhookKeyValueRow[] {
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return [];
    return Object.entries(obj as Record<string, any>).map(([key, value]) => ({
      key,
      value: typeof value === 'string' ? value : JSON.stringify(value)
    }));
  }

  // ── Outbound: formulario estructurado ────────────────────────────────

  private buildConfig(): Record<string, any> {
    const headers = this.rowsToObject(this.outHeaders);
    const body = this.outBodyNonObject !== undefined
      ? this.outBodyNonObject
      : this.rowsToObject(this.outBodyFields, true);
    const config: Record<string, any> = { url: this.outUrl, method: this.outMethod, headers, body };
    if (this.outTimeoutSeconds !== null && this.outTimeoutSeconds !== undefined) {
      config['timeoutSeconds'] = this.outTimeoutSeconds;
    }
    return { ...config, ...this.outExtraConfig };
  }

  private refreshAdvancedJson(): void {
    this.outAdvancedJson = JSON.stringify(this.buildConfig(), null, 2);
    this.outAdvancedJsonError = '';
  }

  /** El usuario abre/cierra el acordeón nativo <details> "Config avanzado (JSON)": sincroniza en ambos sentidos. */
  onAdvancedToggle(event: Event): void {
    const isOpen = (event.target as HTMLDetailsElement).open;
    if (isOpen) {
      this.refreshAdvancedJson();
    } else {
      this.applyAdvancedJson();
    }
    this.outAdvancedOpen = isOpen;
  }

  /** Cambios en el formulario estructurado, mientras el JSON avanzado está abierto, lo refrescan en vivo. */
  onStructuredChange(): void {
    if (this.outAdvancedOpen) this.refreshAdvancedJson();
  }

  /** El usuario edita el JSON avanzado a mano: se refleja de vuelta en el formulario estructurado. */
  onAdvancedJsonChange(): void {
    this.applyAdvancedJson();
  }

  private applyAdvancedJson(): void {
    let parsed: Record<string, any>;
    try {
      parsed = JSON.parse(this.outAdvancedJson || '{}');
    } catch {
      this.outAdvancedJsonError = 'JSON inválido — corrígelo para que se refleje en el formulario.';
      return;
    }
    this.outAdvancedJsonError = '';
    this.outUrl = typeof parsed['url'] === 'string' ? parsed['url'] : '';
    this.outMethod = (parsed['method'] as WorkflowHttpMethod) || 'POST';
    this.outHeaders = this.objectToRows(parsed['headers']);
    const body = parsed['body'];
    if (body && typeof body === 'object' && !Array.isArray(body)) {
      this.outBodyFields = this.objectToRows(body);
      this.outBodyNonObject = undefined;
    } else {
      this.outBodyFields = [];
      this.outBodyNonObject = body;
    }
    this.outTimeoutSeconds = typeof parsed['timeoutSeconds'] === 'number' ? parsed['timeoutSeconds'] : null;
    const { url, method, headers, body: _body, timeoutSeconds, ...rest } = parsed;
    this.outExtraConfig = rest;
  }

  addHeaderRow(): void { this.outHeaders.push({ key: '', value: '' }); this.onStructuredChange(); }
  removeHeaderRow(i: number): void { this.outHeaders.splice(i, 1); this.onStructuredChange(); }
  addBodyRow(): void { this.outBodyFields.push({ key: '', value: '' }); this.onStructuredChange(); }
  removeBodyRow(i: number): void { this.outBodyFields.splice(i, 1); this.onStructuredChange(); }

  // ── Tokens curados (chips) ───────────────────────────────────────────

  get outTokenSuggestions(): WebhookTokenSuggestion[] {
    return WEBHOOK_TOKEN_SUGGESTIONS[this.outObjectType] ?? [];
  }

  get outObjectTypeLabel(): string {
    return this.objectTypeOptions.find(o => o.value === this.outObjectType)?.label ?? this.outObjectType;
  }

  setActiveField(field: ActiveField): void {
    this.activeField = field;
  }

  insertToken(token: string): void {
    const f = this.activeField;
    if (f?.kind === 'url') {
      this.outUrl = (this.outUrl || '') + token;
    } else if (f?.kind === 'header' && this.outHeaders[f.index]) {
      this.outHeaders[f.index].value = (this.outHeaders[f.index].value || '') + token;
    } else if (f?.kind === 'body' && this.outBodyFields[f.index]) {
      this.outBodyFields[f.index].value = (this.outBodyFields[f.index].value || '') + token;
    } else if (f?.kind === 'payload' && this.inPayloadFields[f.index]) {
      this.inPayloadFields[f.index].value = (this.inPayloadFields[f.index].value || '') + token;
      this.onInStructuredChange();
      return;
    } else {
      // Nada enfocado todavía: usamos (o creamos) la última fila del Body como destino por defecto.
      if (this.outBodyFields.length === 0) this.outBodyFields.push({ key: '', value: '' });
      const last = this.outBodyFields[this.outBodyFields.length - 1];
      last.value = (last.value || '') + token;
      this.activeField = { kind: 'body', index: this.outBodyFields.length - 1 };
    }
    this.onStructuredChange();
  }

  // ── Contexto adicional (avanzado) ────────────────────────────────────

  toggleContextAdvanced(): void {
    this.outContextAdvancedOpen = !this.outContextAdvancedOpen;
  }

  // ── Outbound: acciones ────────────────────────────────────────────────

  loadOutboundExample(): void {
    const cfg = WEBHOOK_TEST_EXAMPLE.config as Record<string, any>;
    this.outUrl = cfg['url'] ?? '';
    this.outMethod = (cfg['method'] as WorkflowHttpMethod) ?? 'POST';
    this.outHeaders = this.objectToRows(cfg['headers']);
    this.outBodyFields = this.objectToRows(cfg['body']);
    this.outBodyNonObject = undefined;
    this.outTimeoutSeconds = typeof cfg['timeoutSeconds'] === 'number' ? cfg['timeoutSeconds'] : null;
    const { url, method, headers, body, timeoutSeconds, ...rest } = cfg;
    this.outExtraConfig = rest;
    this.outObjectType = WEBHOOK_TEST_EXAMPLE.objectType;
    this.outContextJson = JSON.stringify(WEBHOOK_TEST_EXAMPLE.context, null, 2);
    if (this.outAdvancedOpen) this.refreshAdvancedJson();
    this.outResult = null;
    this.outError = '';
  }

  testOutbound(): void {
    if (this.isBlockedAction) {
      this.outError = this.outActionType === 'CODE_SNIPPET'
        ? 'La ejecución de Code Snippet requiere un entorno sandbox seguro y aún no está habilitada.'
        : 'La integración nativa seleccionada aún no está configurada. Se registrará como SKIPPED.';
      return;
    }

    if (!this.outUrl || !this.outUrl.trim()) {
      this.outError = 'Ingresa la URL del webhook.';
      return;
    }

    let context: Record<string, any> = {};
    try { context = JSON.parse(this.outContextJson || '{}'); } catch { this.outError = 'El contexto adicional (avanzado) no es un JSON válido.'; return; }

    const config = this.buildConfig();

    this.outLoading = true;
    this.outError = '';
    this.outResult = null;

    this.webhookApi.test({
      actionType: this.outActionType,
      objectType: this.outObjectType,
      ...(this.outObjectId ? { objectId: this.outObjectId } : {}),
      config,
      context
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => { this.outResult = res; this.outLoading = false; },
      error: (err) => {
        const msg: string = err.error?.message ?? '';
        if (err.status === 403) {
          this.outError = 'No tienes permisos para realizar esta acción.';
        } else if (/ticket/i.test(msg)) {
          this.outError = 'No se pudo procesar la solicitud sobre el ticket.';
        } else if (/ssrf|privad|bloque/i.test(msg)) {
          this.outError = 'La URL del webhook no es válida o está bloqueada (URLs internas/privadas no permitidas).';
        } else if (/sandbox|code snippet/i.test(msg)) {
          this.outError = 'La ejecución de Code Snippet requiere un entorno sandbox seguro y aún no está habilitada.';
        } else {
          this.outError = msg || 'No se pudo enviar el webhook externo.';
        }
        this.outLoading = false;
      }
    });
  }

  /** Resumen en lenguaje llano del resultado — reemplaza el volcado crudo de JSON como primera vista. */
  get outSummary(): OutboundSummary | null {
    if (!this.outResult) return null;
    const status = (this.outResult.status ?? '').toUpperCase();
    const output = (this.outResult.output ?? {}) as Record<string, any>;
    const httpStatus: number | undefined = output['httpStatus'];
    const durationMs: number | undefined = output['durationMs'];
    const httpText = httpStatus ? `${httpStatus} ${HTTP_STATUS_TEXT[httpStatus] ?? ''}`.trim() : '';
    const parts = [httpText, durationMs !== undefined ? `${durationMs}ms` : ''].filter(Boolean);
    const suffix = parts.length ? ` (${parts.join(', ')})` : '';

    if (status === 'SUCCESS') {
      return { kind: 'success', title: `Webhook enviado correctamente${suffix}`, detail: this.outResult.message ?? '' };
    }
    if (status === 'SKIPPED') {
      return { kind: 'skipped', title: 'Esta acción no se ejecutó (pendiente de configuración)', detail: this.outResult.message ?? '' };
    }
    if (status === 'FAILED') {
      const errMsg = (this.outResult.error as any)?.message ?? this.outResult.message ?? '';
      return { kind: 'error', title: `El webhook externo respondió con error${suffix}`, detail: errMsg };
    }
    return { kind: 'unknown', title: this.outResult.message ?? 'Resultado desconocido', detail: '' };
  }

  get outHasTechnicalDetail(): boolean {
    if (!this.outResult) return false;
    const out = (this.outResult.output ?? {}) as Record<string, any>;
    const err = (this.outResult.error ?? {}) as Record<string, any>;
    return Object.keys(out).length > 0 || Object.keys(err).length > 0;
  }

  // ── Inbound ─────────────────────────────────────────────────────────

  onEndpointSelected(): void {
    this.inObjectId = null;
    this.inRecords = [];
    const type = this.inSelectedEndpoint?.matchObjectType;
    if (!type) return;
    this.inRecordsLoading = true;
    this.fetchRecordsForType(type).pipe(takeUntil(this.destroy$)).subscribe(records => {
      this.inRecords = records;
      this.inRecordsLoading = false;
    });
  }

  // ── Carga de registros reales (compartida entre outbound e inbound) ──
  private fetchRecordsForType(objectType: string | null | undefined): Observable<RecordOption[]> {
    const businessId = this.businessId;
    switch (objectType) {
      case 'CONTACT':
        return this.contactoApi.searchContacts({ businessId, page: 0, size: 50 }).pipe(
          map((res: any) => (res.content ?? []).map((c: any) => ({ id: c.id, label: c.fullname || c.mail || `Contacto #${c.id}` }))),
          catchError(() => of([]))
        );
      case 'COMPANY':
        return this.empresaApi.searchCompanies({ businessId, page: 0, size: 50 }).pipe(
          map((res: any) => (res.content ?? []).map((c: any) => ({ id: c.id, label: c.commercialName || c.businessName || `Empresa #${c.id}` }))),
          catchError(() => of([]))
        );
      case 'DEAL':
        return this.dealApi.searchDeals({ businessId, page: 0, size: 50 }).pipe(
          map((res: any) => (res.content ?? []).map((d: any) => ({ id: d.id, label: d.name || `Negocio #${d.id}` }))),
          catchError(() => of([]))
        );
      case 'QUOTE':
        return this.quoteApi.searchQuotes({ businessId, page: 0, size: 50 }).pipe(
          map((res: any) => (res.content ?? []).map((q: any) => ({ id: q.id, label: q.quoteNumber || `Cotización #${q.id}` }))),
          catchError(() => of([]))
        );
      default:
        return of([]);
    }
  }

  private setJsonPath(obj: Record<string, any>, jsonPath: string, value: any): void {
    let normalized = jsonPath.startsWith('$') ? jsonPath.substring(1) : jsonPath;
    if (normalized.startsWith('.')) normalized = normalized.substring(1);
    const parts = normalized.split('.').filter(p => p.length > 0);
    if (parts.length === 0) return;
    let cur = obj;
    for (let i = 0; i < parts.length - 1; i++) {
      cur[parts[i]] = cur[parts[i]] || {};
      cur = cur[parts[i]];
    }
    cur[parts[parts.length - 1]] = value;
  }

  // ── Inbound: formulario estructurado (Payload) ───────────────────────

  private buildPayload(): Record<string, any> {
    return this.inPayloadNonObject !== undefined
      ? this.inPayloadNonObject
      : this.rowsToObject(this.inPayloadFields, true);
  }

  private refreshInAdvancedJson(): void {
    this.inAdvancedJson = JSON.stringify(this.buildPayload(), null, 2);
    this.inAdvancedJsonError = '';
  }

  onInAdvancedToggle(event: Event): void {
    const isOpen = (event.target as HTMLDetailsElement).open;
    if (isOpen) {
      this.refreshInAdvancedJson();
    } else {
      this.applyInAdvancedJson();
    }
    this.inAdvancedOpen = isOpen;
  }

  onInStructuredChange(): void {
    if (this.inAdvancedOpen) this.refreshInAdvancedJson();
  }

  onInAdvancedJsonChange(): void {
    this.applyInAdvancedJson();
  }

  private applyInAdvancedJson(): void {
    let parsed: any;
    try {
      parsed = JSON.parse(this.inAdvancedJson || '{}');
    } catch {
      this.inAdvancedJsonError = 'JSON inválido — corrígelo para que se refleje en el formulario.';
      return;
    }
    this.inAdvancedJsonError = '';
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      this.inPayloadFields = this.objectToRows(parsed);
      this.inPayloadNonObject = undefined;
    } else {
      this.inPayloadFields = [];
      this.inPayloadNonObject = parsed;
    }
  }

  addPayloadRow(): void { this.inPayloadFields.push({ key: '', value: '' }); this.onInStructuredChange(); }
  removePayloadRow(i: number): void { this.inPayloadFields.splice(i, 1); this.onInStructuredChange(); }

  /** Al elegir un registro real, actualiza el campo correcto del Payload con su ID. */
  onInObjectIdChange(): void {
    if (!this.inSelectedEndpoint?.matchObjectIdPath || !this.inObjectId) return;
    const current = this.buildPayload();
    const base: Record<string, any> = (current && typeof current === 'object' && !Array.isArray(current)) ? current : {};
    this.setJsonPath(base, this.inSelectedEndpoint.matchObjectIdPath, this.inObjectId);
    this.inPayloadFields = this.objectToRows(base);
    this.inPayloadNonObject = undefined;
    this.onInStructuredChange();
  }

  testInbound(): void {
    if (!this.inSelectedEndpoint) { this.inError = 'Selecciona el endpoint que quieres probar.'; return; }
    const payload = this.buildPayload();

    this.inLoading = true;
    this.inError = '';
    this.inResult = null;

    this.webhookApi.testInbound({
      endpointKey: this.inSelectedEndpoint.endpointKey,
      secret: this.inSecret || undefined,
      payload
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => { this.inResult = res; this.inLoading = false; },
      error: (err) => {
        this.inError = err.error?.message || 'Error al enviar el webhook entrante.';
        this.inLoading = false;
      }
    });
  }

  getStatusClass(status: string | undefined): string {
    switch (status?.toUpperCase()) {
      case 'SUCCESS': return 'bg-green-100 text-green-700';
      case 'FAILED':  return 'bg-red-100 text-red-700';
      case 'SKIPPED': return 'bg-gray-100 text-gray-600';
      default:        return 'bg-blue-100 text-blue-700';
    }
  }
}
